/**
 * Up-front "is this artifact actually in S3?" check for the SIS bundle
 * detail page. Driven by the bundle row alone — no S3 HeadObject calls
 * needed. Lets the UI disable + explain unavailable preview/download
 * buttons instead of waiting for the user to click and hit a 404.
 *
 * The rules mirror what the bundler actually writes (see
 * services/api/src/handlers/sisBundles/bundler.ts +
 * services/api/src/handlers/common/sisBundleSubmit.ts). Keep this file
 * in sync if the bundler changes which artifacts it produces.
 */

import {
  isSisBundleTerminal,
  type CanvasSyncSisBundle,
  type SisBundleArtifactName,
  type SisBundleInputFile,
} from "@canvas-sync/shared";

export interface ArtifactAvailability {
  available: boolean;
  /** Human-friendly explanation when unavailable. Surfaced inline so
   *  operators know WHY a button is greyed out. */
  reason?: string;
}

export function computeArtifactAvailability(
  bundle: CanvasSyncSisBundle,
  name: SisBundleArtifactName | SisBundleInputFile,
): ArtifactAvailability {
  switch (name) {
    // ─── Always-present-once-validated artifacts ──────────────────────
    case "manifest":
    case "SJDC_SR_CANVAS_ACCOUNTS.csv":
    case "SJDC_SR_CANVAS_COURSES.csv":
    case "SJDC_SR_CANVAS_ENROLLMENTS.csv":
    case "SJDC_SR_CANVAS_SECTIONS.csv":
    case "SJDC_SR_CANVAS_USERS.csv":
    case "SJDC_SR_CANVAS_LOGINS.csv":
      // archiveInputs() runs after VALIDATING (PASS). Anything pre-
      // validation or that failed validation has no archive copy.
      if (bundle.status === "RECEIVED") {
        return { available: false, reason: "Bundle hasn't validated yet" };
      }
      if (bundle.status === "VALIDATING") {
        return { available: false, reason: "Validation in progress" };
      }
      if (
        bundle.status === "INCOMPLETE_INPUTS" ||
        bundle.status === "INVALID_INPUTS"
      ) {
        return {
          available: false,
          reason:
            bundle.status === "INCOMPLETE_INPUTS"
              ? "Declared PS files weren't in S3 — nothing was archived"
              : "Hashes didn't match the manifest — nothing was archived",
        };
      }
      return { available: true };

    // ─── Validation artifact — written even on FAIL ───────────────────
    case "validation":
      // validateInputs() writes validation.json before returning, even
      // when status=FAIL. Only RECEIVED bundles haven't reached it.
      if (bundle.status === "RECEIVED") {
        return { available: false, reason: "Validation hasn't started yet" };
      }
      return { available: true };

    // ─── Worker fix CSV ───────────────────────────────────────────────
    case "fix":
      // Bundler archives the fix CSV whenever the worker produced one
      // (fixFileS3Uri populated on the row), even when the bundler
      // skipped including it due to CONTRADICTION / INPUT_DRIFT.
      if (bundle.fixFileS3Uri) return { available: true };
      return { available: false, reason: reasonForNoFix(bundle) };

    // ─── Contradiction sample ─────────────────────────────────────────
    case "contradiction-sample":
      if (bundle.contradictionSampleS3Uri) return { available: true };
      if (bundle.fixSkipReason === "CONTRADICTION") {
        return {
          available: false,
          reason: "Sample artifact wasn't written for this contradiction",
        };
      }
      return { available: false, reason: "No contradictions to sample" };

    // ─── Submitted zip ────────────────────────────────────────────────
    case "zip":
      // Bundler builds + archives the zip BEFORE the Canvas POST, so any
      // status that got to BUNDLING has it. SKIPPED_UNCHANGED short-
      // circuits before the zip build, so it doesn't.
      if (bundle.submittedZipS3Uri) return { available: true };
      return { available: false, reason: reasonForNoZip(bundle) };

    // ─── Canvas response ──────────────────────────────────────────────
    case "canvas-response":
      // Archived only when fetch() was actually attempted. Dry-run,
      // skip-unchanged, and safety-guard rejections all bypass the POST.
      if (bundle.canvasResponseS3Uri) return { available: true };
      return { available: false, reason: reasonForNoCanvasResponse(bundle) };

    // ─── Result summary ───────────────────────────────────────────────
    case "result":
      // writeResultJson runs for all terminal paths EXCEPT
      // INCOMPLETE_INPUTS / INVALID_INPUTS (which terminate before
      // buildAndSubmitSisBundle is ever called).
      if (
        bundle.status === "INCOMPLETE_INPUTS" ||
        bundle.status === "INVALID_INPUTS"
      ) {
        return {
          available: false,
          reason: "Bundle failed validation — no result was written",
        };
      }
      if (!isSisBundleTerminal(bundle.status)) {
        return { available: false, reason: "Bundle is still in progress" };
      }
      return { available: true };
  }
}

function reasonForNoFix(bundle: CanvasSyncSisBundle): string {
  switch (bundle.fixSkipReason) {
    case "NO_GHOSTS_FOUND":
      return "Worker ran cleanly and found no ghosts";
    case "NO_FIX_GENERATED":
      return "Ghost-detection run failed before producing a fix";
    case "NO_ENROLLMENTS_FILE":
      return "This drop had no enrollments file, so ghost detection didn't run";
    case "GHOST_DETECTION_TIMEOUT":
      return "Ghost detection didn't finish in time — no fix was archived";
    default:
      if (
        bundle.status === "RECEIVED" ||
        bundle.status === "VALIDATING" ||
        bundle.status === "WAITING_FOR_GHOST_DETECTION"
      ) {
        return "Waiting for ghost detection to complete";
      }
      return "No fix file was produced for this bundle";
  }
}

function reasonForNoZip(bundle: CanvasSyncSisBundle): string {
  switch (bundle.status) {
    case "RECEIVED":
    case "VALIDATING":
    case "WAITING_FOR_GHOST_DETECTION":
    case "BUNDLING":
    case "POSTING":
      return "Zip hasn't been built yet — bundle is still in progress";
    case "INCOMPLETE_INPUTS":
    case "INVALID_INPUTS":
      return "Validation failed before the zip could be built";
    case "SKIPPED_UNCHANGED":
      return "Skipped (unchanged) — no zip was built because payload matched the last successful submission";
    case "SKIPPED_NO_FIX":
      return "Ghost-fix-only mode with no safe fix — no zip was built";
    default:
      return "No zip was archived for this bundle";
  }
}

function reasonForNoCanvasResponse(bundle: CanvasSyncSisBundle): string {
  if (bundle.dryRun || bundle.status === "DRY_RUN_BUILT") {
    return "Dry-run mode — no Canvas POST was attempted";
  }
  if (bundle.status === "SKIPPED_UNCHANGED") {
    return "Skipped (unchanged) — no Canvas POST was attempted";
  }
  if (bundle.status === "SKIPPED_GHOST_DETECTION_TIMEOUT") {
    return "Bundle timed out before any Canvas POST";
  }
  if (bundle.status === "SKIPPED_NO_FIX") {
    return "Ghost-fix-only mode with no safe fix — no Canvas POST was attempted";
  }
  if (
    bundle.status === "FAILED" &&
    bundle.errorMessage?.includes("ALLOW_PROD_CANVAS_POST")
  ) {
    return "Blocked by the production safety guard — no POST was attempted";
  }
  if (!isSisBundleTerminal(bundle.status)) {
    return "Canvas POST hasn't been attempted yet";
  }
  return "No Canvas response was archived for this bundle";
}
